import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import MaterialIcon from '../../elements/MaterialIcon.jsx';
import DateHelper from '../../../utilities/DateHelper';
import { isMonthSelected, monthsBetween, selectionBounds } from '../../../utilities/monthSelection.js';

/**
 * A year of months to pick from. A plain click picks one month. A range is two clicks with Range
 * switched on, or a shift-click, which stretches the selection out to the month clicked.
 *
 * `monthsWithData` holds the yearMonthSerialize() keys of months that have a budget, drawn as a dot.
 * A press outside `boundaryRef` (the picker itself when there is none) closes it, so the button that
 * opened it can toggle it shut instead of closing and reopening it.
 */
export default function MonthPicker({ selectedDates, today = moment(), monthsWithData = new Set(), onSelect, onSelectRange, onClose, boundaryRef = null }) {
	const bounds = selectionBounds(selectedDates);
	const [year, setYear] = useState(() => (bounds ? bounds.last.year() : today.year()));
	const [rangeMode, setRangeMode] = useState(false);
	const [rangeStart, setRangeStart] = useState(null);
	const containerRef = useRef(null);

	useEffect(() => {
		const onKeyDown = (event) => {
			if(event.key === 'Escape') {
				onClose();
			}
		};
		const onPointerDown = (event) => {
			const boundary = (boundaryRef || containerRef).current;
			if(boundary && !boundary.contains(event.target)) {
				onClose();
			}
		};
		document.addEventListener('keydown', onKeyDown);
		document.addEventListener('mousedown', onPointerDown);
		document.addEventListener('touchstart', onPointerDown);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.removeEventListener('mousedown', onPointerDown);
			document.removeEventListener('touchstart', onPointerDown);
		};
	}, [onClose, boundaryRef]);

	const pick = useCallback((month, event) => {
		if(rangeMode) {
			if(!rangeStart) {
				setRangeStart(month);
				return;
			}
			onSelectRange(rangeStart, month);
			onClose();
			return;
		}
		if(event.shiftKey && bounds) {
			const months = [bounds.first, bounds.last, month];
			const sorted = months.slice().sort((a, b) => (a.year() * 12 + a.month()) - (b.year() * 12 + b.month()));
			onSelectRange(sorted[0], sorted[2]);
			onClose();
			return;
		}
		onSelect(month);
		onClose();
	}, [rangeMode, rangeStart, bounds, onSelect, onSelectRange, onClose]);

	const toggleRange = useCallback(() => {
		setRangeMode((on) => !on);
		setRangeStart(null);
	}, []);

	const months = monthsBetween(moment([year, 0, 1]), moment([year, 11, 1]));

	return (
		<div className='month-picker' ref={containerRef} role='dialog' aria-label='Choose a month'>
			<div className='month-picker__year'>
				<button type='button' className='shell-control month-picker__step' aria-label='Previous year' onClick={() => setYear(year - 1)}>
					<MaterialIcon>chevron_left</MaterialIcon>
				</button>
				<span className='month-picker__year-label'>{year}</span>
				<button type='button' className='shell-control month-picker__step' aria-label='Next year' onClick={() => setYear(year + 1)}>
					<MaterialIcon>chevron_right</MaterialIcon>
				</button>
			</div>
			<div className='month-picker__grid'>
				{months.map((month) => {
					const key = DateHelper.yearMonthSerialize(month);
					const classes = ['shell-control', 'month-picker__month'];
					if(isMonthSelected(selectedDates, month)) {
						classes.push('is-selected');
					}
					if(month.isSame(today, 'month')) {
						classes.push('is-today');
					}
					if(rangeStart && month.isSame(rangeStart, 'month')) {
						classes.push('is-range-start');
					}
					return (
						<button
							type='button'
							key={key}
							data-month={key}
							className={classes.join(' ')}
							aria-pressed={isMonthSelected(selectedDates, month)}
							onClick={(event) => pick(month, event)}
						>
							{month.format('MMM')}
							{monthsWithData.has(key) && <span className='month-picker__dot' aria-hidden='true' />}
						</button>
					);
				})}
			</div>
			<div className='month-picker__footer'>
				<button
					type='button'
					className='shell-control month-picker__action'
					onClick={() => {
						onSelect(today.clone().startOf('month'));
						onClose();
					}}
				>
					This month
				</button>
				<button
					type='button'
					className={`shell-control month-picker__action month-picker__range ${rangeMode ? 'is-on' : ''}`}
					aria-pressed={rangeMode}
					onClick={toggleRange}
				>
					<MaterialIcon>date_range</MaterialIcon>
					{rangeMode ? (rangeStart ? 'Pick the last month' : 'Pick the first month') : 'Range'}
				</button>
			</div>
			<div className='month-picker__hint'>Shift-click a month to stretch the selection to it.</div>
		</div>
	);
}
